import { FC, ReactElement, memo, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import { useAppModalControls } from '@/common/hooks';
import { BaseQueryParams } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';

import { SearchConsoleAnalyticsDomainsQueryKeys, useGetByIdSearchConsoleAnalyticsDomainsQuery, useGetSearchConsoleAnalyticsMutation } from '@/modules/Domains';

import { DescriptionBlock } from '@/common/components';
import { SearchConsoleAnalyticsForm } from '../SearchConsoleAnalyticsForm';
import { SearchConsoleAnalyticsResult } from '../SearchConsoleAnalyticsResult';

interface Props {
  siteId: string;
  domainId: string;
}

export const SearchConsoleAnalyticsDossierContainer: FC<Props> = memo(({ domainId, siteId }) => {
  const queryClient = useQueryClient();
  const [resultModalCotrols] = useAppModalControls();
  const [analyticsResult, setAnalyticsResult] = useState<(BaseQueryParams<typeof useGetSearchConsoleAnalyticsMutation>['data']) | null>(null);

  const {
    error: searchConsoleDomainAnalyticsError,
    isError: isSearchConsoleDomainAnalyticsError,
    isFetching: isSearchConsoleDomainAnalyticsFetching,
  } = useGetByIdSearchConsoleAnalyticsDomainsQuery(
    siteId,
    domainId
  )({
    enabled: !!domainId && !!siteId,
    onError: async () => {
      await queryClient.refetchQueries([SearchConsoleAnalyticsDomainsQueryKeys.GetAll, siteId], { exact: false });
    },
  });

  useEffect(() => {
    resultModalCotrols.close();
    setAnalyticsResult(null);
  }, [domainId]);

  const handleCollectAnalytics = (data: typeof analyticsResult): void => {
    setAnalyticsResult(data);
  };

  return (
    <FallbackedLayout
      isLoading={ isSearchConsoleDomainAnalyticsFetching }
      isError={ isSearchConsoleDomainAnalyticsError }
      error={ searchConsoleDomainAnalyticsError }
    >
      {
        (): ReactElement => (
          <>
            <SearchConsoleAnalyticsForm
              domainId={ domainId }
              siteId={ siteId }
              handleCollectAnalytics={ handleCollectAnalytics }
            />

            { analyticsResult?.responseAggregationType && <DescriptionBlock
              layout="horizontal"
              items={ [['Тип збирання', analyticsResult.responseAggregationType]] }
            />
            }

            <SearchConsoleAnalyticsResult
              analyticsResult={ analyticsResult }
              modalCotrols={ resultModalCotrols }
            />
          </>
        )
      }
    </FallbackedLayout>
  );
});
